import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type SyncState = 'synced' | 'syncing' | 'pending' | 'error' | 'paused';

interface SyncStats {
  pending: number;
  synced: number;
  conflicts: number;
  errors: number;
  lastSyncAt?: string;
}

interface SyncStore {
  // State
  syncState: SyncState;
  progress: number; // 0-100
  stats: SyncStats;
  isPaused: boolean;
  lastError?: string;

  // Actions
  setSyncState: (state: SyncState) => void;
  setProgress: (progress: number) => void;
  setStats: (stats: SyncStats) => void;
  setPaused: (paused: boolean) => void;
  setLastError: (error?: string) => void;
  reset: () => void;
}

const initialStats: SyncStats = {
  pending: 0,
  synced: 0,
  conflicts: 0,
  errors: 0,
};

export const useSyncStore = create<SyncStore>()(
  persist(
    (set) => ({
      // Initial state
      syncState: 'synced',
      progress: 0,
      stats: initialStats,
      isPaused: false,
      lastError: undefined,

      // Actions
      setSyncState: (syncState) => set({ syncState }),

      setProgress: (progress) => set({ progress }),

      setStats: (stats) => set({ stats }),

      setPaused: (isPaused) => {
        set({ isPaused });
        if (isPaused) {
          set({ syncState: 'paused' });
        }
      },

      setLastError: (lastError) => {
        set({ lastError });
        if (lastError) {
          set({ syncState: 'error' });
        }
      },

      reset: () =>
        set({
          syncState: 'synced',
          progress: 0,
          stats: initialStats,
          isPaused: false,
          lastError: undefined,
        }),
    }),
    {
      name: 'sync-store',
      partialize: (state) => ({
        stats: state.stats,
        lastError: state.lastError,
      }),
    }
  )
);
